import { Map as MapIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { DeployedUnit, Phase } from "../../api/types";
import { Button } from "../../components/ui";
import { config } from "../../config";
import { t } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { UnitSymbol } from "../orbats/UnitSymbol";

/** Unité citée par l'OPO (survol, demande d'Alexis du 09/10/2026) : noms sous lesquels l'ordre la nomme, position
 *  du temps lu, symbole et mission. */
export interface UnitMention {
  code: string;
  labels: string[];
  name: string;
  sidc: string;
  side: DeployedUnit["side"];
  lngLat: [number, number];
  mission: string | null;
  locality: string | null;
}

/** Unités de la carte que l'ordre peut citer : désignation affichée, désignation et code (jamais le code générique
 *  « 111({F})… » de l'ennemi), position du temps `phase` (sinon T1). */
export function unitMentions(units: readonly DeployedUnit[] | undefined, phase: Phase): UnitMention[] {
  const out: UnitMention[] = [];
  for (const u of units ?? []) {
    const pos = u.positions[phase]?.valid ? u.positions[phase] : u.positions.T1;
    if (!pos || !pos.valid) continue;
    const labels = [...new Set([u.display_name, u.designation, u.code].filter((l): l is string => Boolean(l && l.length >= 3 && !l.includes("{"))))];
    if (!labels.length) continue;
    out.push({
      code: u.code,
      labels,
      name: u.display_name || u.designation || u.code,
      sidc: u.sidc,
      side: u.side,
      lngLat: [pos.lon, pos.lat],
      mission: pos.mission_label || pos.mission || null,
      locality: pos.locality,
    });
  }
  return out;
}

/** Carte statique centrée sur l'unité (style de la carte de Generex) ; rien sans jeton Mapbox. */
function staticMapUrl([lon, lat]: [number, number]): string | null {
  if (!config.mapboxToken) return null;
  const style = config.mapboxStyle.replace(/^mapbox:\/\/styles\//, "");
  const center = `${lon.toFixed(5)},${lat.toFixed(5)},12.3,0`;
  return `https://api.mapbox.com/styles/v1/${style}/static/${center}/300x168@2x?attribution=false&logo=false&access_token=${config.mapboxToken}`;
}

const WIDTH = 300;
const HEIGHT_ESTIMATE = 262;

/** Mini-carte d'une unité citée, posée sous le nom survolé (au-dessus faute de place), jamais hors de l'écran ;
 *  elle reste ouverte tant que la souris est sur le nom ou sur elle. « Voir sur la carte » ouvre la carte entière
 *  centrée sur l'unité. */
export function UnitPeek({
  mention,
  anchor,
  onEnter,
  onLeave,
  onShowOnMap,
}: {
  mention: UnitMention;
  anchor: DOMRect;
  onEnter: () => void;
  onLeave: () => void;
  onShowOnMap: (mention: UnitMention) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const url = useMemo(() => staticMapUrl(mention.lngLat), [mention.lngLat]);
  useEffect(() => setLoaded(false), [url]);
  const below = anchor.bottom + 8 + HEIGHT_ESTIMATE < window.innerHeight;
  const top = below ? anchor.bottom + 8 : Math.max(8, anchor.top - 8 - HEIGHT_ESTIMATE);
  const left = Math.min(Math.max(8, anchor.left + anchor.width / 2 - WIDTH / 2), window.innerWidth - WIDTH - 8);
  const enemy = mention.side === "ENI";
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={t("Unité {name}", { name: mention.name })}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="peek-in fixed z-[70] overflow-hidden rounded-xl border border-line bg-surface shadow-lg"
      style={{ top, left, width: WIDTH }}
    >
      <button
        type="button"
        onClick={() => onShowOnMap(mention)}
        className="relative block h-[168px] w-full bg-canvas"
        aria-label={t("Voir {name} sur la carte", { name: mention.name })}
      >
        {url && (
          <img
            src={url}
            alt=""
            width={WIDTH}
            height={168}
            draggable={false}
            onLoad={() => setLoaded(true)}
            className={`h-full w-full object-cover transition-opacity duration-(--gx-enter) ${loaded ? "opacity-100" : "opacity-0"}`}
          />
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="relative grid place-items-center">
            <span className={`peek-ring absolute size-14 rounded-full ${enemy ? "bg-hostile/25" : "bg-accent/25"}`} />
            <UnitSymbol sidc={mention.sidc} size={22} className="relative drop-shadow" />
          </span>
        </span>
      </button>
      <div className="space-y-1 px-3.5 pt-2.5 pb-3">
        <p className="truncate text-ui font-semibold text-ink">{mention.name}</p>
        {(mention.mission || mention.locality) && (
          <p className="line-clamp-2 text-meta leading-snug text-ink-soft">
            {[mention.mission ? unitMissionName(mention.mission) : null, mention.locality].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="flex justify-end pt-1">
          <Button size="sm" variant="secondary" onClick={() => onShowOnMap(mention)}>
            <MapIcon size={14} /> {t("Voir sur la carte")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Survol d'un nom d'unité, partagé par l'OPO, la mise en ambiance et les ordres subordonnés : petite intention de
 *  survol avant d'ouvrir la mini-carte, délai avant de la refermer pour laisser la souris l'atteindre. */
export function useUnitPeek(onShowOnMap: (mention: UnitMention) => void) {
  const [peek, setPeek] = useState<{ mention: UnitMention; anchor: DOMRect } | null>(null);
  const timer = useRef<number | null>(null);
  const keep = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  const hover = (mention: UnitMention | null, anchor: DOMRect | null) => {
    keep();
    if (mention && anchor) timer.current = window.setTimeout(() => setPeek({ mention, anchor }), 180);
    else timer.current = window.setTimeout(() => setPeek(null), 220);
  };
  useEffect(() => () => keep(), []);
  const element = peek ? (
    <UnitPeek
      mention={peek.mention}
      anchor={peek.anchor}
      onEnter={keep}
      onLeave={() => hover(null, null)}
      onShowOnMap={(m) => {
        setPeek(null);
        onShowOnMap(m);
      }}
    />
  ) : null;
  return { hover, element };
}

/** Expression des noms d'unités (le plus long d'abord), jamais au milieu d'un mot ni d'un code. */
export function mentionMatcher(mentions: readonly UnitMention[]) {
  const labels = mentions.flatMap((u, i) => u.labels.map((label) => [label, i] as const));
  if (!labels.length) return null;
  labels.sort((a, b) => b[0].length - a[0].length);
  const index = new Map(labels.map(([label, i]) => [label, i]));
  const alternatives = labels.map(([label]) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  return { re: new RegExp(`(?<![\\p{L}\\p{N}(])(${alternatives})(?![\\p{L}\\p{N}])`, "gu"), index };
}
